import type { CellCoord, Direction, FormedWord, MoveInput, MoveValidation, RuleTile } from '../types';
import { BOARD_SIZE, cellsCovered, directionStep, tileFit } from './board';

const ALL_AXES: Direction[] = ['h', 'v', 'dr', 'ur'];

/** The axes a run is read along. */
const wordAxes = (diagonals: boolean): Direction[] => (diagonals ? ALL_AXES : ['h', 'v']);

const key = (row: number, col: number) => `${row},${col}`;

type LetterCell = CellCoord & { letter: string; locked: boolean };

/** One entry per covered cell, each carrying the character of its tile that sits there. */
const letterCells = (tiles: RuleTile[], locked: boolean): LetterCell[] =>
  tiles.flatMap((tile) =>
    cellsCovered(tile).map((c, i) => ({ ...c, letter: (tile.letter[i] ?? '').toUpperCase(), locked })),
  );

/** Position of a cell along an axis: the same for every cell on one line. */
const lineOf = (c: CellCoord, axis: Direction): number =>
  axis === 'h' ? c.row : axis === 'v' ? c.col : axis === 'dr' ? c.row - c.col : c.row + c.col;

/**
 * The one axis every cell sits on, or `null` where they sit on no single line. A lone cell sits on all four and keeps
 * `fallback`, its own tile's direction.
 */
function axisThrough(cells: LetterCell[], fallback: Direction): Direction | null {
  const candidates = ALL_AXES.filter((axis) => cells.every((c) => lineOf(c, axis) === lineOf(cells[0], axis)));
  if (candidates.length === 0) return null;
  return candidates.includes(fallback) ? fallback : candidates[0];
}

/** Walks back to the first occupied cell of the run through `from`, then forward to its end. */
function runThrough(grid: Map<string, LetterCell>, from: CellCoord, axis: Direction): LetterCell[] {
  const { dr, dc } = directionStep(axis);
  let row = from.row;
  let col = from.col;
  while (grid.has(key(row - dr, col - dc))) {
    row -= dr;
    col -= dc;
  }
  const run: LetterCell[] = [];
  let cell = grid.get(key(row, col));
  while (cell) {
    run.push(cell);
    row += dr;
    col += dc;
    cell = grid.get(key(row, col));
  }
  return run;
}

/**
 * Validates one player's move against the tiles already on the board, checking `docs/game-rules.md` section 3 in
 * order and reporting the first rule broken:
 *
 * - every tile fits: on the grid, off holes, on no other tile and, with diagonals off, no cluster tile on a diagonal;
 * - every cluster tile shares one direction, and every tile lies on one line along it (a single letter declares no
 *   direction: one cell reads the same whichever way it faces);
 * - the line from the first to the last placed cell has no empty cell (tiles on the board may fill gaps);
 * - an opening move covers the start square, and every later move forms a word through a tile on the board;
 * - every word formed is accepted by `isWord`.
 *
 * Words are maximal runs of two or more letters through a placed cell, read along every axis the game reads: rows and
 * columns, and with `diagonals` both diagonals too.
 */
export function validateMove({
  shape,
  size = BOARD_SIZE,
  locked,
  placed,
  isWord,
  diagonals,
  start,
}: MoveInput): MoveValidation {
  if (placed.length === 0) return { valid: false, reason: 'no-tiles', words: [] };

  const board = [...locked, ...placed];
  for (const tile of placed) {
    const misfit = tileFit(tile, shape, board, diagonals, size);
    if (misfit) return { valid: false, reason: misfit, words: [] };
  }

  const newCells = letterCells(placed, false);

  // Only a tile with extent declares a direction.
  const spanning = placed.filter((t) => t.letter.length > 1);
  const declared = spanning[0]?.dir;
  if (declared && spanning.some((t) => t.dir !== declared)) {
    return { valid: false, reason: 'mixed-directions', words: [] };
  }

  const axis = declared ?? axisThrough(newCells, placed[0].dir);
  if (!axis) return { valid: false, reason: 'not-in-line', words: [] };

  const line = lineOf(newCells[0], axis);
  if (newCells.some((c) => lineOf(c, axis) !== line)) return { valid: false, reason: 'not-in-line', words: [] };

  const grid = new Map<string, LetterCell>();
  for (const c of [...letterCells(locked, true), ...newCells]) grid.set(key(c.row, c.col), c);

  // Along every axis the column advances or holds, so ordering by column (then row) orders the line.
  const ordered = [...newCells].sort((a, b) => a.col - b.col || a.row - b.row);
  const mainRun = runThrough(grid, ordered[0], axis);
  const last = ordered[ordered.length - 1];
  if (!mainRun.some((c) => c.row === last.row && c.col === last.col)) {
    return { valid: false, reason: 'gap-in-line', words: [] };
  }

  const isOpening = locked.length === 0;
  if (isOpening && !newCells.some((c) => c.row === start.row && c.col === start.col)) {
    return { valid: false, reason: 'misses-start', words: [] };
  }

  const words: FormedWord[] = [];
  const seen = new Set<string>();
  const addRun = (run: LetterCell[], runAxis: Direction) => {
    if (run.length < 2) return;
    const id = `${runAxis}:${key(run[0].row, run[0].col)}`;
    if (seen.has(id)) return;
    seen.add(id);
    const word = run.map((c) => c.letter).join('');
    words.push({
      word,
      axis: runAxis,
      cells: run.map(({ row, col }) => ({ row, col })),
      valid: isWord(word, runAxis),
      usesLocked: run.some((c) => c.locked),
    });
  };

  const axes = wordAxes(diagonals);
  // The move's own run spells only along an axis that is read.
  if (axes.includes(axis)) addRun(mainRun, axis);
  for (const cell of newCells) {
    for (const cross of axes) {
      if (cross !== axis) addRun(runThrough(grid, cell, cross), cross);
    }
  }

  if (words.length === 0) return { valid: false, reason: 'no-word-formed', words };
  if (!isOpening && !words.some((w) => w.usesLocked)) return { valid: false, reason: 'not-connected', words };
  if (words.some((w) => !w.valid)) return { valid: false, reason: 'not-a-word', words };

  return { valid: true, words };
}
